import React, { useRef, useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import useUpload from '../../hooks/useUpload';
import { XIcon, UserIcon } from '@heroicons/react/solid';
import Button from './Button';
import InlineLoader from '../loader/InlineLoader';

const SelectImage = ({
  control,
  name,
  rules,
  label = 'Upload Image',
  className,
  initialPreview,
  onRemove,
  isRemoving = false, 
}) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(initialPreview);
  const [isUploading, setIsUploading] = useState(false);
  const [highlightUploadBox, setHighlightUploadBox] = useState(false);

  useEffect(() => {
    if (initialPreview) {
      setPreview(initialPreview);
    }
  }, [initialPreview]);

  const upload = useUpload({
    url: '/uploadImage',
    paramName: 'thumbnail[]',
    onSuccess: (response) => {
      setIsUploading(false);
      if (response?.data?.data?.[0]) {
        const { image_id, image_url } = response.data.data[0];
        setPreview(image_url);
        control._formValues[name] = { image_id, image_url };
        toast.success('Image uploaded successfully');
      } else {
        // Reset to initialPreview on error
        setPreview(initialPreview);
        toast.error('Failed to process uploaded image');
      }
    },
    onError: (error) => {
      setIsUploading(false);
      // Reset to initialPreview on error
      setPreview(initialPreview);
      toast.error('Failed to upload image');
    }
  });

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const dragStart = (e) => {
    e.preventDefault();
    setHighlightUploadBox(true);
  };

  const dragOver = (e) => {
    e.preventDefault();
  };

  const dragEnter = (e) => {
    e.preventDefault();
    setHighlightUploadBox(true);
  };

  const dragLeave = (e) => {
    e.preventDefault();
    setHighlightUploadBox(false);
  };

  const createPreview = (file) => {
    return URL.createObjectURL(file);
  };

  return (
    <div className={className}>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const handleFileChange = (file) => {
            if (!file) return;

            if (!file.type.startsWith('image')) {
              toast.error('Please select an image file');
              return;
            }

            const previewUrl = createPreview(file);
            setPreview(previewUrl);
            setIsUploading(true);

            upload.mutate({
              file,
              screen: 'Profile'
            });

            onChange(file);
          };

          const dropFile = (e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            setHighlightUploadBox(false);
            handleFileChange(file);
          };

          const removeFile = () => {
            const imageId = value?.image_id;

            // For newly uploaded images that haven't been saved yet
            if (!imageId) {
              // Just clear the preview and form value locally
              setPreview(null);
              onChange('');
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
              return;
            }

            // Call the API to remove saved image
            if (onRemove) {
              onRemove(imageId);
            }
          };

          return (
            <>
              <input
                className="hidden"
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={(e) => handleFileChange(e.target.files[0])}
              />

              <div
                onDragStart={dragStart}
                onDragOver={dragOver}
                onDragEnter={dragEnter}
                onDragLeave={dragLeave}
                onDrop={dropFile}
                className="relative"
              >
                <div
                  className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center ${
                    highlightUploadBox ? 'border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {preview || value ? (
                    <div className="relative flex flex-col items-center">
                      <div className="relative">
                        <img
                          src={preview || (typeof value === 'object' && value.image_url ? value.image_url : (typeof value === 'string' ? value : ''))}
                          alt="Profile"
                          className="h-24 w-24 rounded-full object-cover"
                        />

                        {/* Remove the Delete button during removal */}
                        {!isRemoving && (
                          <Button
                            size="icon"
                            variant="ghost"
                            type="button"
                            onClick={removeFile}
                            className="absolute -top-2 -right-4 bg-red-500 hover:!bg-red-600 rounded-full p-1 text-white"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={handleClick}
                        className="mt-4"
                        disabled={isUploading || isRemoving}
                      >
                        {isUploading ? (
                          <>
                            <InlineLoader className="mr-2" /> Uploading...
                          </>
                        ) : isRemoving ? (
                          <>
                            <InlineLoader className="mr-2" /> Removing...
                          </>
                        ) : (
                          "Change Photo"
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="mx-auto h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                        <UserIcon className="h-12 w-12 text-gray-400" />
                      </div>
                      <p className="mb-2 text-sm text-gray-500">{label}</p>
                      <p className="mb-4 text-xs text-gray-400">Click to browse or drag and drop</p>
                      <Button
                        type="button"
                        onClick={handleClick}
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <>
                            <InlineLoader className="mr-2" /> Uploading...
                          </>
                        ) : (
                          "Select Photo"
                        )}
                      </Button>
                    </div>
                  )}
                </div>
                {error && <p className="mt-2 text-red-500 text-sm">{error.message}</p>}
              </div>
            </>
          );
        }}
      />
    </div>
  );
};

export default SelectImage;
